// Confirm dialogs for archiving / deleting spaces and collections (v0.3).
import { useEffect, useState } from "react";
import { ApiError, errorMessage } from "../api";
import type { BindingSummary, CollectionSummary, Space } from "../api/types";
import { connectorName, supportsArchive } from "../lib/archive";
import {
  deleteCollectionById,
  deleteSpaceById,
  loadConnectors,
  setCollectionArchived,
  setSpaceArchived,
  toastError,
} from "../store/app";
import { useApp } from "../store/hooks";
import { Modal } from "./bits";
import { EnableBucketsDialog } from "./BucketDialogs";

export type LifecycleTarget =
  | { kind: "archive-collection"; collection: CollectionSummary }
  | { kind: "archive-space"; space: Space; collections: CollectionSummary[] }
  | { kind: "delete-collection"; collection: CollectionSummary }
  | { kind: "delete-space"; space: Space; collections: CollectionSummary[] }
  | { kind: "enable-buckets"; collection: CollectionSummary };

function useManifests() {
  const manifests = useApp((s) => s.connectors);
  useEffect(() => {
    void loadConnectors();
  }, []);
  return manifests;
}

/** Bindings of the collections, with connector support for archiving. */
function BindingList({ bindings, verb }: { bindings: (BindingSummary & { collection: string })[]; verb: "archive" | "delete" }) {
  const manifests = useManifests();
  if (!bindings.length) return null;
  return (
    <ul className="binding-effects">
      {bindings.map((b) => {
        const tool = connectorName(b.connector_type, manifests);
        const sup = supportsArchive(b.connector_type, manifests);
        let effect: string;
        if (verb === "delete") effect = `stays untouched in ${tool}; the link is removed`;
        else if (sup) effect = `can also be archived in ${tool} (unarchiving restores it)`;
        else if (sup === false) effect = `${tool} doesn't support archiving — it stays active there`;
        else effect = `${tool}: archive support unknown`;
        return (
          <li key={b.id}>
            <strong>{b.remote_collection_name}</strong>
            {b.collection !== b.remote_collection_name && <span className="muted"> ({b.collection})</span>} — {effect}
          </li>
        );
      })}
    </ul>
  );
}

function bindingsOf(cols: CollectionSummary[]) {
  return cols.flatMap((c) => c.bindings.map((b) => ({ ...b, collection: c.name })));
}

export function ArchiveDialog({
  target,
  onClose,
}: {
  target: Extract<LifecycleTarget, { kind: "archive-collection" | "archive-space" }>;
  onClose: () => void;
}) {
  const manifests = useManifests();
  const cols = target.kind === "archive-collection" ? [target.collection] : target.collections;
  const bindings = bindingsOf(cols);
  const supportedTools = [
    ...new Set(
      bindings
        .filter((b) => supportsArchive(b.connector_type, manifests) !== false)
        .map((b) => connectorName(b.connector_type, manifests)),
    ),
  ];
  const [remote, setRemote] = useState(true);
  const [busy, setBusy] = useState(false);
  const name = target.kind === "archive-collection" ? target.collection.name : target.space.name;

  return (
    <Modal title={target.kind === "archive-space" ? `Archive space “${name}”?` : `Archive “${name}”?`} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            const r = supportedTools.length ? remote : true;
            if (target.kind === "archive-collection") await setCollectionArchived(target.collection.id, true, r);
            else await setSpaceArchived(target.space.id, true, r);
            onClose();
          } catch (err) {
            toastError(err, "Archive failed");
            setBusy(false);
          }
        }}
      >
        <p>
          {target.kind === "archive-space"
            ? `All ${cols.length} collection${cols.length === 1 ? "" : "s"} in this space are archived. `
            : ""}
          Archived collections stay readable but are hidden from the sidebar, My tasks and meetings.{" "}
          <strong>Sync pauses</strong> until you unarchive.
        </p>
        <BindingList bindings={bindings} verb="archive" />
        {supportedTools.length > 0 && (
          <label className="check-row">
            <input type="checkbox" checked={remote} onChange={(e) => setRemote(e.target.checked)} />
            <span>Also archive in {supportedTools.join(" and ")}</span>
          </label>
        )}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy} autoFocus>
            {busy ? "Archiving…" : "Archive"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function DeleteDialog({
  target,
  onClose,
  onArchiveInstead,
  onDeleted,
}: {
  target: Extract<LifecycleTarget, { kind: "delete-collection" | "delete-space" }>;
  onClose: () => void;
  onArchiveInstead?: () => void;
  onDeleted?: () => void;
}) {
  const isSpace = target.kind === "delete-space";
  const cols = isSpace ? target.collections : [target.collection];
  const name = isSpace ? target.space.name : target.collection.name;
  const archived = isSpace ? !!target.space.archived_at : !!target.collection.archived_at;
  const bindings = bindingsOf(cols);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = !isSpace || typed.trim() === name.trim();

  return (
    <Modal title={isSpace ? `Delete space “${name}”?` : `Delete “${name}”?`} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!canDelete) return;
          setBusy(true);
          setError(null);
          try {
            if (isSpace) await deleteSpaceById(target.space.id);
            else await deleteCollectionById(target.collection.id);
            onClose();
            onDeleted?.();
          } catch (err) {
            if (err instanceof ApiError && err.status === 409) {
              setError(
                isSpace
                  ? "This is the last space of the organization and can't be deleted. Create another space first."
                  : errorMessage(err),
              );
            } else setError(errorMessage(err));
            setBusy(false);
          }
        }}
      >
        <p>
          {isSpace
            ? `The space, its ${cols.length} collection${cols.length === 1 ? "" : "s"} and all their items are removed from ToDoItAll.`
            : "The collection and all its items are removed from ToDoItAll."}{" "}
          <strong>Nothing is deleted in connected tools</strong>
          {bindings.length ? " — their links are removed:" : "."}
        </p>
        <BindingList bindings={bindings} verb="delete" />
        {isSpace && (
          <label className="field">
            <span>
              Type <strong>{name}</strong> to confirm
            </span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus autoComplete="off" />
          </label>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions wrap">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          {onArchiveInstead && !archived && (
            <button type="button" className="btn" onClick={onArchiveInstead}>
              Archive instead
            </button>
          )}
          <button type="submit" className="btn danger-solid" disabled={busy || !canDelete} autoFocus={!isSpace}>
            {busy ? "Deleting…" : isSpace ? "Delete space" : "Delete collection"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Renders the dialog for a lifecycle target and lets "Archive instead" switch to the archive dialog. */
export function LifecycleDialog({
  target,
  onChange,
  onDeleted,
}: {
  target: LifecycleTarget;
  onChange: (t: LifecycleTarget | null) => void;
  onDeleted?: (t: LifecycleTarget) => void;
}) {
  const close = () => onChange(null);
  if (target.kind === "enable-buckets") {
    return <EnableBucketsDialog collection={target.collection} onClose={close} />;
  }
  if (target.kind === "archive-collection" || target.kind === "archive-space") {
    return <ArchiveDialog key={target.kind} target={target} onClose={close} />;
  }
  const archiveInstead = () => {
    const bound = (target.kind === "delete-space" ? target.collections : [target.collection]).some((c) => c.bindings.length);
    if (target.kind === "delete-space") {
      if (bound) onChange({ kind: "archive-space", space: target.space, collections: target.collections });
      else {
        close();
        void setSpaceArchived(target.space.id, true, true).catch((e) => toastError(e, "Archive failed"));
      }
    } else if (bound) onChange({ kind: "archive-collection", collection: target.collection });
    else {
      close();
      void setCollectionArchived(target.collection.id, true, true).catch((e) => toastError(e, "Archive failed"));
    }
  };
  return <DeleteDialog key={target.kind} target={target} onClose={close} onArchiveInstead={archiveInstead} onDeleted={() => onDeleted?.(target)} />;
}
